import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSession } from '@/shared/auth';
import { supabase } from '@/lib/supabase';
import {
  applyMotion,
  readStoredMotion,
  resolveMotion,
  storeMotion,
  watchSystemMotion,
  type MotionPreference,
} from './applyMotion';

export interface MotionContextValue {
  /** What the user chose: true/false, or null for "follow the system". */
  preference: MotionPreference;
  /** What is applied right now. */
  enabled: boolean;
  setPreference: (preference: MotionPreference) => void;
}

export const MotionContext = createContext<MotionContextValue | null>(null);

export function MotionProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const hasLocalPreference = useRef(false);
  const [preference, setPreferenceState] = useState<MotionPreference>(() => {
    const stored = readStoredMotion();
    hasLocalPreference.current = stored !== null;
    return stored;
  });
  const [enabled, setEnabled] = useState<boolean>(() => resolveMotion(preference));
  const profileWriteVersion = useRef(0);
  const userId = session.status === 'signedIn' ? session.session.user.id : null;

  useEffect(() => {
    if (!userId || hasLocalPreference.current) return;
    let active = true;
    const requestVersion = profileWriteVersion.current;
    void supabase
      .from('profiles')
      .select('motion_enabled')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!active || requestVersion !== profileWriteVersion.current || hasLocalPreference.current) return;
        if (typeof data?.motion_enabled !== 'boolean') return;
        setPreferenceState(data.motion_enabled);
        storeMotion(data.motion_enabled);
      });
    return () => {
      active = false;
    };
  }, [userId]);

  // The inline script in index.html already applied the stored choice before
  // first paint, so this never causes a flash — it only keeps the DOM in
  // sync afterwards (e.g. once profiles.motion_enabled arrives).
  useEffect(() => {
    setEnabled(applyMotion(preference));
  }, [preference]);

  // Following the system must track prefers-reduced-motion live, not only at boot.
  useEffect(() => {
    if (preference !== null) return;
    return watchSystemMotion(() => setEnabled(applyMotion(null)));
  }, [preference]);

  const setPreference = useCallback((next: MotionPreference) => {
    profileWriteVersion.current += 1;
    hasLocalPreference.current = next !== null;
    storeMotion(next);
    setPreferenceState(next);
    if (userId) void supabase.from('profiles').update({ motion_enabled: next }).eq('id', userId);
  }, [userId]);

  const value = useMemo<MotionContextValue>(
    () => ({ preference, enabled, setPreference }),
    [preference, enabled, setPreference],
  );

  return <MotionContext.Provider value={value}>{children}</MotionContext.Provider>;
}
